import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { User } from '../api/ester-b2b-api/private/users/types'
import { Question, ScreeningStatusResponse, Services } from '@estercare/ester-shared'
import { HealthCallAppointment } from '../api/ester-b2b-api/private/healthCalls/types'
import { FeatureFlags } from '../api/ester-b2b-api/private/featureFlags/types'

type ScreeningStoreState = {
  screeningData?: ScreeningStatusResponse
  setScreeningData: (data: ScreeningStatusResponse) => void
  resetState: () => void
}

type ServiceStoreState = {
  activeServices: Services[]
  setActiveServices: (services: Services[]) => void
}
type ScreeningQuestionnaireStoreState = {
  currentAnswer: string[] | null
  currentQuestion: Question | null
  /** Answers posted so far this session, by question id. Nothing can read an answer back out of
   *  the questionnaire API once posted, so steps after the last question read them from here. */
  answers: Record<string, string[]>
  setCurrentQuestion: (question: Question) => void
  recordAnswer: (questionId: string, answer: string[]) => void
  updateCurrentAnswer: (answer: string[]) => void
  resetState: () => void
}

export type UserStoreState = {
  user?: User
  setUser: (user: User) => void
}

export type FeatureFlagsStoreState = {
  featureFlags?: FeatureFlags
  isLoaded: boolean
  setFeatureFlags: (featureFlags: FeatureFlags) => void
  isFeatureEnabled: (flagId: string) => boolean
}

type CertificationStoreState = {
  screeningId?: string
  policyDocuments: File[]
  setScreeningId: (screeningId: string) => void
  setPolicyDocuments: (policyDocuments: File[]) => void
  resetState: () => void
}

type HealthCallStoreState = {
  healthCallAppointment?: HealthCallAppointment | null
  setHealthCallAppointment: (appointment: HealthCallAppointment | null) => void
  bookingFormData?: {
    personalNumber: string
    phoneNumber: string
  }
  setBookingFormData: (data: { personalNumber: string; phoneNumber: string }) => void
  resetState: () => void
}

export const useUserStore = create<UserStoreState>()(
  devtools((set) => ({
    user: undefined,
    setUser: (user) => set({ user }),
  }))
)

export const useFeatureFlagsStore = create<FeatureFlagsStoreState>()(
  devtools((set, get) => ({
    featureFlags: undefined,
    isLoaded: false,
    setFeatureFlags: (featureFlags) => set({ featureFlags, isLoaded: true }),
    isFeatureEnabled: (flagId: string) => {
      const flags = get().featureFlags
      if (!flags) return false
      return flags[flagId]?.toLowerCase() === 'true'
    },
  }))
)

export const useServicesStore = create<ServiceStoreState>()(
  devtools((set) => ({
    activeServices: [],
    setActiveServices: (activeServices) =>
      set({
        activeServices,
      }),
  }))
)

export const useScreeningStore = create<ScreeningStoreState>()(
  devtools((set) => ({
    screeningData: undefined,
    setScreeningData: (data) => set({ screeningData: data }),
    resetState: () => set({ screeningData: undefined }),
  }))
)

export const useScreeningQuestionnaireStore = create<ScreeningQuestionnaireStoreState>()(
  devtools((set) => ({
    currentQuestion: null,
    currentAnswer: null,
    answers: {},

    setCurrentQuestion: (question) => {
      set({
        currentQuestion: question,
      })
    },

    recordAnswer: (questionId, answer) => set((state) => ({ answers: { ...state.answers, [questionId]: answer } })),

    updateCurrentAnswer: (answer) => {
      set((state) => {
        if (!state.currentQuestion) return state

        const updatedQuestion = {
          ...state.currentQuestion,
          answer,
        }

        return {
          currentQuestion: updatedQuestion,
        }
      })
    },

    resetState: () =>
      set({
        currentQuestion: null,
        answers: {},
      }),
  }))
)

export const useCertificationStore = create<CertificationStoreState>()(
  devtools((set) => ({
    screeningId: undefined,
    policyDocuments: [],
    setScreeningId: (screeningId) => set({ screeningId }),
    setPolicyDocuments: (policyDocuments) => set({ policyDocuments }),
    resetState: () => set({ screeningId: undefined, policyDocuments: [] }),
  }))
)

export const useHealthCallStore = create<HealthCallStoreState>()(
  devtools((set) => ({
    healthCallAppointment: null,
    bookingFormData: undefined,
    setHealthCallAppointment: (healthCallAppointment) => set({ healthCallAppointment }),
    setBookingFormData: (bookingFormData) => set({ bookingFormData }),
    resetState: () =>
      set({
        healthCallAppointment: null,
        bookingFormData: undefined,
      }),
  }))
)
